import React, { useEffect, useState, useRef, useCallback } from 'react';
import 'antd/dist/antd.css';
import { Row, Col, Button, Select, DatePicker, Modal, Checkbox } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import { InfoWindow, Marker, Polygon } from '@react-google-maps/api';
import { useSelector } from 'react-redux';
import { Loading } from 'components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import 'moment/locale/tr';
import locale from 'antd/es/date-picker/locale/tr_TR';
import { MAP_DEFAULT_VALUES } from 'helpers/constants';
import Orders from './Orders';
import {
  getCouriersByZoneIdForRouting,
  getOrdersBySlotIdForRouting,
  isRoutingComplete,
  routeOrdersWithCouriers,
  setSelectedRoutingDetailData,
} from 'store/requests/routing';
import { getGeoRadarZones } from 'store/requests/zone';
import { getSlotsByZoneId } from 'store/requests/routing';
import Couriers from './Couriers';
import userHaveRolePermission from '../../../../helpers/userHaveRolePermission';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import PastOrders from './PastOrders';
import { PARENT_ROUTES, PAGE_URLS } from 'helpers/endpoints';
import { GoogleMap } from '@react-google-maps/api';

const { Option } = Select;
const RoutingPlanning = ({ route }) => {
  const history = useHistory();
  const selectedRoutingDetailData =
    useSelector((state) => state?.routing?.selectedRoutingDetailData) ?? null;
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled || !selectedRoutingDetailData) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const map = useRef();
  const today = new Date();
  const nextTwoDays = new Date(today);
  nextTwoDays.setDate(today.getDate() + 2);

  const [loading, setLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [infoWindow, setInfoWindow] = useState(false);
  const [stores, setStores] = useState([]);
  const [zones, setZones] = useState([]);
  const [slots, setSlots] = useState([]);
  const [orders, setOrders] = useState([]);
  const [pastOrders, setPastOrders] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [storeInputVisible, setStoreInputVisible] = useState(false);
  const [isLoadingZones, setIsLoadingZones] = useState(false);
  const [selectedZoneId, setSelectedZoneId] = useState(zones[0]?.id);
  const [selectedZoneInfo, setSelectedZoneInfo] = useState(zones[0]?.id);
  const [selectedOrders, setSelectedOrders] = useState(null);
  const [selectedPastOrders, setSelectedPastOrders] = useState(null);
  const [orderMarkers, setOrderMarkers] = useState(null);
  const [pastOrderMarkers, setPastOrderMarkers] = useState(null);
  const [selectedCouriers, setSelectedCouriers] = useState([]);
  const [showPastOrders, setShowPastOrders] = useState(false);
  const [selectedStoreID, setSelectedStoreID] = useState(null);
  const [storeLatLon, setStoreLatLon] = useState([]);
  const userRegions = useSelector((state) => state?.definitions?.userRegions);
  const userStores = useSelector((state) => state?.definitions?.userStores);

  const [selectedSlot, setSelectedSlot] = useState([]);

  const dateFormat = 'DD/MM/YYYY';
  const containerStyle = {
    width: '100%',
    height: '81vh',
  };

  const activeRegion = useSelector((state) => state.auth?.user?.activeRegion);
  const activeStore = useSelector((state) => state.auth?.user?.activeStore);

  const defaultMapPosition = (zoom = MAP_DEFAULT_VALUES.ZOOM) => {
    if (!map.current) {
      return;
    }

    map.current.setZoom(zoom);
    map.current.setCenter({
      lat: MAP_DEFAULT_VALUES.LAT,
      lng: MAP_DEFAULT_VALUES.LNG,
    });
  };

  function handleSlotSelect(value) {
    const selectedSlot = slots.find((slot) => slot.slotId === value);
    const duplicates = slots
      .filter((slot) => slot.beginTime === selectedSlot.beginTime && slot.endTime === selectedSlot.endTime)
      .map((duplicate) => duplicate.slotId);
    setSelectedSlot(duplicates);
  }

  function currentAndNextSlots() {
    if (slots.length > 0) {
      const nextOneSlot = slots.filter((slot) => slot.status === 'NEXT_ONE');
      const currentSlots = slots.filter((slot) => slot.status === 'CURRENT');
      const followingSlots = slots.filter((slot) => slot.status === 'FOLLOWING');

      const slotMap = new Map();

      const updateSlotMap = (slot) => {
        const key = slot.beginTime + '-' + slot.endTime;
        const value = slot.slotId;


        if (!slotMap.has(key)) {
          slotMap.set(key, value);
        }
      };

      currentSlots.forEach(updateSlotMap);

      nextOneSlot.forEach(updateSlotMap);

      followingSlots.slice(0, 4).forEach(updateSlotMap);

      const options = [];

      slotMap.forEach((slotIds, timeInterval) => {
        options.push(
          <Option key={timeInterval} value={slotIds}>
            {timeInterval}
          </Option>
        );
      });

      return options;
    }
  }

  const onMapLoaded = (loadedMap) => {
    map.current = loadedMap;
    defaultMapPosition();
  };

  const handleChangeStore = (value) => {
    setIsLoadingZones(true);
    setZones([]);
    setSlots([]);
    setSelectedSlot([]);
    setSelectedStoreID(value);
    getGeoRadarZones(`filter=store.id=in=(${value})&size=2000`)
      .then((zones) => {
        const  zoneResponseDtoList  = zones.content ?? [];
        setZones(zoneResponseDtoList);
        setSelectedZoneId(zoneResponseDtoList[0]?.id);
        setStoreLatLon([
          zoneResponseDtoList[0]?.store.latitude,
          zoneResponseDtoList[0]?.store.longitude,
        ]);
        setSelectedZoneInfo(zoneResponseDtoList[0]);
        getCouriersByZoneIdForRouting(zoneResponseDtoList[0]?.id).then(
          (couriers) => {
            setCouriers(couriers);
          },
        );
        getSlotsByZoneId(zoneResponseDtoList[0]?.id).then((slots) => {
          setSlots(slots?.data ? slots?.data : []);
        });
        setIsLoadingZones(false);
      })
      .finally(() => {
        setIsLoadingZones(false);
      });
  };

  useEffect(() => {
    setOrders([]);
    setSelectedOrders([]);
    setOrderMarkers([]);
    setShowPastOrders(false);
  }, [selectedSlot, selectedZoneId]);

  useEffect(() => {
    if (selectedZoneInfo?.zonePolygonJson) {
      getBoundsZonePolygon(selectedZoneInfo);
    }
  }, [selectedZoneInfo]);

  const getBoundsZonePolygon = (selectedZoneInfo) => {
    const bounds = new window.google.maps.LatLngBounds();
    if (selectedZoneInfo) {
      selectedZoneInfo.zonePolygonJson?.forEach((point) => {
        bounds?.extend(point);
      });
    }
    map.current?.fitBounds(bounds);
  };

  const handleChangeZone = (value) => {
    setSelectedSlot([]);
    setShowPastOrders(false);
    setSlots([]);
    setOrders([]);
    getSlotsByZoneId(value).then((slots) => {
      setSlots(slots?.data ? slots?.data : []);
    });
    getCouriersByZoneIdForRouting(value).then((couriers) => {
      setCouriers(couriers);
    });
    setSelectedZoneId(value);
    const selectedZone = zones.find((zone) => zone.id === value);
    setSelectedZoneInfo(selectedZone);
  };

  useEffect(() => {
    if (activeRegion) {
      setStoreInputVisible(false);

      const storesOfRegion =
        userRegions.find((region) => region.id === activeRegion.id)?.children ||
        [];
      setStores(storesOfRegion);
      setStoreInputVisible(true);
    } else {
      setStoreInputVisible(false);
    }
  }, [activeRegion, userRegions]);

  useEffect(() => {
    if (selectedSlot.length > 0) {
      setLoading(true);

      Promise.all([
        getOrdersBySlotIdForRouting({
          slotIdList: [...selectedSlot],
          previousDayNotRoutedOrdersIncluded: false,
          zoneId: selectedZoneId,
        }),
        getOrdersBySlotIdForRouting({
          slotIdList: [...selectedSlot],
          previousDayNotRoutedOrdersIncluded: true,
          zoneId: selectedZoneId,
        }),
      ])
        .then(([currentOrders, allOrders]) => {
          const curOrders = currentOrders;

          const allPastOrders = allOrders.filter((order) => {
            return !curOrders.some(
              (currentOrder) => currentOrder.orderNumber === order.orderNumber,
            );
          });

          setOrders(currentOrders);
          setPastOrders(allPastOrders);
          setLoading(false);
        })
        .catch((error) => {
          console.error('Hata:', error);
          setLoading(false);
        });
    }
  }, [selectedSlot]);

  useEffect(() => {
    if (activeStore) {
      getGeoRadarZones(`filter=store.id=in=(${activeStore.id})&size=2000`)
        .then((zones) => {
          const zoneResponseDtoList = zones.content ?? [];
          setZones(zoneResponseDtoList);
          setSelectedZoneId(zoneResponseDtoList[0]?.id);
          setSelectedZoneInfo(zoneResponseDtoList[0]);
          setStoreLatLon([
            zoneResponseDtoList[0]?.store.latitude,
            zoneResponseDtoList[0]?.store.longitude,
          ]);
          //sayfa tekrar aktif olana kadar kullanılmayacak
         /* getSlotsByZoneId(zoneResponseDtoList[0]?.id).then((slots) => {
            setSlots(slots?.data);
          });*/
          getCouriersByZoneIdForRouting(zoneResponseDtoList[0]?.id).then(
            (couriers) => {
              setCouriers(couriers);
            },
          );
          setIsLoadingZones(false);
        })
        .finally(() => {
          setIsLoadingZones(false);
        });
    }
  }, [activeStore]);

  useEffect(() => {
    if (slots.length > 0) {
      const nextOneSlot = slots.find((slot) => slot.status === 'NEXT_ONE');
      if (nextOneSlot) {
        setSelectedSlot([nextOneSlot.slotId]);
      }
    }
  }, [slots]);

  useEffect(() => {
    const newMarkers = selectedOrders?.map((row) => ({
      position: {
        lat: row.latitude,
        lng: row.longitude,
      },
    }));
    setOrderMarkers(newMarkers);
  }, [selectedOrders]);

  useEffect(() => {
    const newMarkers = selectedPastOrders?.map((row) => ({
      position: {
        lat: row.latitude,
        lng: row.longitude,
      },
    }));
    setPastOrderMarkers(newMarkers);
  }, [selectedPastOrders]);


  const disabledDate = (current) => {
    return !current.isSame(today, 'day');
  };

  const routingOrders = () => {
    let couriers = [];

    const selectedOrdersId = selectedOrders?.map((order) => order.id);
    const selectedPastOrdersId = selectedPastOrders?.map((order) => order.id);

    if (selectedPastOrdersId?.length > 0) {
      const findedSlot = slots.find((slot) => selectedSlot.includes(slot.slotId));
      const slotString = `${findedSlot.beginTime}-${findedSlot.endTime}`;
      const confirmationMessage = t('youHavePastOrders', { slotString });
      Modal.confirm({
        title: t('doYouConfirm'),
        content: confirmationMessage,
        onOk: () => {
          setLoading(true);
          setButtonLoading(true);
          const allOrdersId = [...selectedOrdersId, ...selectedPastOrdersId];

          selectedCouriers.map((selectedCourier) => {
            const newCourier = {
              id: selectedCourier.courierId,
              lateDuration: selectedCourier.delay ? selectedCourier.delay : 0,
            };
            couriers.push(newCourier);
          });

          performRouting(allOrdersId, couriers);
        },
        onCancel: () => {},
      });
    } else {
      setLoading(true);
      setButtonLoading(true);
      selectedCouriers.map((selectedCourier) => {
        const newCourier = {
          id: selectedCourier.courierId,
          lateDuration: selectedCourier.delay ? selectedCourier.delay : 0,
        };
        couriers.push(newCourier);
      });

      performRouting(selectedOrdersId, couriers);
    }
  };
  const performRouting = async (selectedOrdersId, couriers) => {
    try {
      const res = await routeOrdersWithCouriers({
        orders: selectedOrdersId,
        couriers: couriers,
        slotId: selectedSlot[0],
      });
      setSelectedRoutingDetailData({
        id: res.data.routeId,
        taskId: res.data.taskId,
        zoneId: selectedZoneId,
        storeId: activeStore?.id ?? selectedStoreID,
        approved: false,
        storeLatLon: storeLatLon,
      });

      setTimeout(async () => {
        const isCompletedRes = await isRoutingComplete(res.data.taskId);
        if (isCompletedRes.data.isCompleted) {
          history.push(
            `${PARENT_ROUTES.ROUTING_MANAGEMENT}${PAGE_URLS.ROUTING_DETAIL}`,
          );
          setLoading(false);
          setButtonLoading(false);
        } else {
          const intervalId = setInterval(async () => {
            const isCompletedRes = await isRoutingComplete(res.data.taskId);
            if (isCompletedRes.data.isCompleted) {
              clearInterval(intervalId);
              history.push(
                `${PARENT_ROUTES.ROUTING_MANAGEMENT}${PAGE_URLS.ROUTING_DETAIL}`,
              );
              setLoading(false);
              setButtonLoading(false);
            }
          }, 10000);
        }
      }, 0);
    } catch (error) {
      console.error('Hata:', error);
      setLoading(false);
      setButtonLoading(false);
    }
  };

  return (
    <div>
      <Row
        gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}
        style={{ paddingBottom: '1rem' }}
        align="middle"
      >
        <Col xxl={2} xl={3}>
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col span={8}>
              <Button
                type="primary"
                icon={<EnvironmentOutlined />}
                onClick={routingOrders}
                loading={buttonLoading}
                disabled={
                  !(
                    (selectedOrders?.length > 0 ||
                      selectedPastOrders?.length > 0) &&
                    selectedCouriers?.length > 0
                  )
                }
              >
                {t('route')}
              </Button>
            </Col>
          </Row>
        </Col>
        <Col xxl={19} xl={18}>
          <Row gutter={8} align="middle">
            {storeInputVisible && (
              <Col span={6}>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Mağaza Seçiniz"
                  onChange={handleChangeStore}
                  showSearch
                  filterOption={true}
                  optionFilterProp="children"
                >
                  {stores.map((store, index) => (
                    <Option key={index} value={store.id}>
                      {store.title}
                    </Option>
                  ))}
                </Select>
              </Col>
            )}
            <Col span={6}>
              <Select
                style={{ width: '100%' }}
                placeholder="Zone Seçiniz"
                onChange={handleChangeZone}
                showSearch
                filterOption={true}
                optionFilterProp="children"
                value={selectedZoneId}
                loading={isLoadingZones}
              >
                {zones.map((zone, index) => (
                  <Option key={index} value={zone.id}>
                    {zone.name}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col className="gutter-row route_t2" span={3}>
              <DatePicker
                locale={locale}
                style={{ width: '100%' }}
                placeholder="Tarih Seçin"
                defaultValue={moment()}
                format={dateFormat}
                disabledDate={disabledDate}
              />
            </Col>

            {slots && (
              <Col className="gutter-row" span={3}>
                <Select
                  style={{ width: '100%' }}
                  onChange={handleSlotSelect}
                  placeholder={t('selectSlot')}
                  value={selectedSlot}
                  maxTagCount="responsive"
                  allowClear
                >
                  {currentAndNextSlots()}
                </Select>
              </Col>
            )}
          </Row>
        </Col>
      </Row>
      <Row gutter={10} className="mapWrapper">
        <Col xxl={7} xl={8} className="gutter-row">
          <Checkbox
            disabled={!selectedSlot.length > 0}
            onChange={(e) => setShowPastOrders(e.target.checked)}
            checked={showPastOrders}
          >
            {t('showPastOrders')}
          </Checkbox>
          {showPastOrders && selectedSlot.length > 0 && (
            <PastOrders
              pastOrders={pastOrders}
              setSelectedOrdersForPast={setSelectedPastOrders}
              selectedSlot={selectedSlot}
              zoneId={selectedZoneId}
            />
          )}
          <Orders
            orders={orders}
            selectedSlot={selectedSlot}
            setSelectedOrders={setSelectedOrders}
            showPastOrders={showPastOrders}
            zoneId={selectedZoneId}
          />
        </Col>
        <Col xxl={11} xl={9} className="gutter-row route_t7">
          {loading && (
            <div className="loadingDiv">
              <Loading />
            </div>
          )}

          <GoogleMap mapContainerStyle={containerStyle} onLoad={onMapLoaded}>
            {infoWindow && (
              <InfoWindow
                onCloseClick={() => setInfoWindow(null)}
                position={infoWindow}
              >
                <div className="markerInfoBox">
                  <div className="markerInfoBox-header">
                    <p className="order-info-text">{t('orderId') + ':'}</p>
                    <Link
                      to={
                        '/siparis-yonetimi/siparis/duzenle/' +
                        infoWindow.orderId
                      }
                      target="_blank"
                    >
                      {infoWindow.orderId}
                    </Link>
                  </div>
                </div>
              </InfoWindow>
            )}

            {orderMarkers?.length > 0 &&
              orderMarkers.map((selectedOrder, index) => (
                <Marker
                  key={index}
                  position={selectedOrder.position}
                  icon={'/static/img/locationConfirmed.svg'}
                />
              ))}
            {pastOrderMarkers?.length > 0 &&
              pastOrderMarkers.map((pastOrder, index) => (
                <Marker
                  key={index}
                  position={pastOrder.position}
                  icon={'/static/img/locationConfirmed.svg'}
                />
              ))}
            {selectedZoneInfo?.zonePolygonJson && (
              <Polygon
                paths={selectedZoneInfo?.zonePolygonJson}
                options={{
                  strokeOpacity: 1,
                  fillColor: 'lightgreen',
                  strokeColor: 'green',
                  editable: false,
                  fillOpacity: 0.4,
                }}
              />
            )}
            {
              <Marker
                position={{
                  lat: storeLatLon[0],
                  lng: storeLatLon[1],
                }}
                icon="/static/img/ceptesokpin.svg"
              />
            }
          </GoogleMap>
        </Col>
        <Col xxl={6} xl={7} className="gutter-row">
          <Couriers
            couriers={couriers}
            setSelectedCouriers={setSelectedCouriers}
          />
        </Col>
      </Row>
    </div>
  );
};

export default RoutingPlanning;
